// Categorieën als filter (MOW-CATEGORIEFILTER-OKT26, punt N9): de boom uit het hoofdmenu, het kiezen van
// meerdere categorieën tegelijk, en de regels waarmee een product bij een keuze past.
//
// Bewust zonder imports en met alleen wegstripbare TypeScript, zodat Node dit bestand rechtstreeks laadt
// voor de test (zelfde patroon als lib/filter-query.ts en lib/klaviyo-core.ts). Een import toevoegen breekt
// scripts/category-filter.test.mjs.
//
// Regels (besluiten NB8 uit PLAN-feedbacklijst-oktober-2026.md, aanbevolen opties):
// - Meerdere categorieën zijn OF: een product telt mee als het bij één van de gekozen categorieën hoort.
// - Een gekozen categorie omvat zijn onderliggende categorieën. Dat gaat vanzelf: elk product draagt in
//   `categorySlugs` alle voorouders van zijn categorie (gemeten op 269/269 zichtbare producten).
// - "Nachtmode" is geen echte categorie maar de vier nachtkleding-takken; kiezen betekent een van die vier.

/** De vorm van `/api/navigation` (app/api/navigation/route.ts), alleen wat hier nodig is. */
export interface MenuLinkLike { label: string; href: string; count?: number }
export interface MenuColumnLike { title: string; href?: string; links: MenuLinkLike[] }
export interface MenuItemLike { label: string; href: string; columns?: MenuColumnLike[] }

export interface CategoryNode {
  slug: string;
  label: string;
  children: CategoryNode[];
}

/** De velden van een product waar de categorieregels op werken (structureel een deel van Product). */
export interface CategoryProductLike {
  categorySlugs?: ReadonlyArray<unknown> | null;
  subcategorySlug?: string | null;
  categorySlug?: string | null;
  colorOptions?: ReadonlyArray<unknown> | null;
}

export const NACHTMODE_SLUG = "nachtmode";
/** Dezelfde vier takken als NACHTMODE_TAKKEN in lib/product-list-data.ts en app/api/navigation/route.ts (een test bewaakt dat). */
export const NACHTMODE_TAKKEN: ReadonlyArray<string> = [
  "dames-nachtkleding", "heren-nachtkleding", "jongens-nachtkleding", "meisjes-nachtkleding",
];

// Map in plaats van een object: een sleutel als "__proto__" of "constructor" mag niets doen.
const VIRTUAL_MATCH = new Map<string, ReadonlyArray<string>>([
  // Twee producten dragen de slug "nachtmode" zelf (Frogs & Dogs), die tellen ook mee.
  [NACHTMODE_SLUG, [NACHTMODE_SLUG, ...NACHTMODE_TAKKEN]],
]);

/** De vaste hoofdcategorieën zoals het hoofdmenu ze toont (zonder Merken en Sale): de blokken op /shop en de eerste weergave van de boom. */
export const HOOFD_CATEGORIEEN: ReadonlyArray<CategoryNode> = [
  { slug: "dames", label: "Dames", children: [] },
  { slug: "heren", label: "Heren", children: [] },
  { slug: "kinderen", label: "Kinderen", children: [] },
  { slug: NACHTMODE_SLUG, label: "Nachtmode", children: [] },
  { slug: "thermo", label: "Thermo", children: [] },
  { slug: "unisex", label: "Unisex", children: [] },
];

/** De blokken op de pagina Nachtmode, voordat het menu binnen is (de pagina zelf heeft geen subcategorieën in Firestore). */
export const NACHTMODE_BLOKKEN: ReadonlyArray<CategoryNode> = [
  { slug: "dames-nachtkleding", label: "Dames nachtkleding", children: [] },
  { slug: "heren-nachtkleding", label: "Heren nachtkleding", children: [] },
  { slug: "jongens-nachtkleding", label: "Jongens nachtkleding", children: [] },
  { slug: "meisjes-nachtkleding", label: "Meisjes nachtkleding", children: [] },
];

const MAX_SLUG_LENGTH = 80;

/** De categorie-slug uit een menulink (`/product-category/dames/dames-nachtkleding` geeft `dames-nachtkleding`); leeg als de link geen categorie is (Merken, Sale). */
export function slugFromHref(href: unknown): string {
  if (typeof href !== "string") return "";
  const m = /^\/product-category\/([^?#]+)/.exec(href);
  if (!m) return "";
  const parts = m[1].split("/").filter(Boolean);
  const last = parts[parts.length - 1] ?? "";
  try {
    return decodeURIComponent(last).slice(0, MAX_SLUG_LENGTH);
  } catch {
    return "";
  }
}

/** Een slug zoals hij in het adres mag staan: kleine letters, cijfers en streepjes. Leeg als er niets van overblijft. */
export function cleanSlug(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, MAX_SLUG_LENGTH);
}

function nodeFromLink(link: MenuLinkLike | null | undefined): CategoryNode | null {
  const slug = slugFromHref(link?.href);
  if (!slug || typeof link?.label !== "string") return null;
  return { slug, label: link.label, children: [] };
}

function findNode(nodes: ReadonlyArray<CategoryNode>, slug: string, needChildren = false): CategoryNode | undefined {
  for (const n of nodes) {
    if (n.slug === slug && (!needChildren || n.children.length > 0)) return n;
    const deeper = findNode(n.children, slug, needChildren);
    if (deeper) return deeper;
  }
  return undefined;
}

/**
 * De categorieboom uit het hoofdmenu: hoofditem > kolom (subcategorie) > link. Merken en Sale hebben geen
 * categoriepagina en vallen weg. De kolom van Nachtmode heeft geen eigen pagina ("Nachtkleding"): haar links
 * worden direct kinderen van Nachtmode, en elke tak krijgt de laag eronder van dezelfde slug elders in de boom
 * (Nachtmode > Dames nachtkleding > Nachthemden), zodat de menukeuze hier gelijk is aan die bij Dames.
 */
export function categoryTreeFromMenu(items: ReadonlyArray<MenuItemLike> | null | undefined): CategoryNode[] {
  const roots: CategoryNode[] = [];
  for (const item of items ?? []) {
    const slug = slugFromHref(item?.href);
    if (!slug || typeof item.label !== "string") continue;
    const node: CategoryNode = { slug, label: item.label, children: [] };
    for (const col of item.columns ?? []) {
      const colSlug = slugFromHref(col?.href);
      const links = (col?.links ?? []).map(nodeFromLink).filter((n): n is CategoryNode => !!n);
      if (colSlug && typeof col.title === "string") {
        node.children.push({ slug: colSlug, label: col.title, children: links });
      } else {
        node.children.push(...links);
      }
    }
    roots.push(node);
  }
  for (const root of roots) {
    if (!VIRTUAL_MATCH.has(root.slug)) continue;
    const others = roots.filter((r) => r !== root);
    root.children = root.children.map((tak) => {
      const elders = findNode(others, tak.slug, true);
      return elders ? { ...tak, children: elders.children } : tak;
    });
  }
  return roots;
}

function collectSlugs(node: CategoryNode, out: Set<string>): void {
  for (const c of node.children) {
    out.add(c.slug);
    collectSlugs(c, out);
  }
}

/** Alle slugs onder een categorie (kinderen, kleinkinderen, ...), ook als dezelfde slug op twee plekken in de boom staat. */
export function descendantSlugs(tree: ReadonlyArray<CategoryNode>, slug: string): Set<string> {
  const out = new Set<string>();
  const visit = (nodes: ReadonlyArray<CategoryNode>) => {
    for (const n of nodes) {
      if (n.slug === slug) collectSlugs(n, out);
      visit(n.children);
    }
  };
  visit(tree);
  return out;
}

/** De lagen onder een categorie, voor een pagina die zelf die categorie is. `undefined` als de slug niet in de boom staat. */
export function rootedTree(tree: ReadonlyArray<CategoryNode>, rootSlug: string): CategoryNode[] | undefined {
  return findNode(tree, rootSlug)?.children;
}

/** Alle categorie-slugs van een product, ook uit de oude losse velden voor producten die nog niet gemigreerd zijn. */
export function productCategorySlugs(p: CategoryProductLike | null | undefined): string[] {
  const out: string[] = [];
  const add = (v: unknown) => {
    if (typeof v === "string" && v) out.push(v);
  };
  for (const s of p?.categorySlugs ?? []) add(s);
  add(p?.subcategorySlug);
  add(p?.categorySlug);
  return out;
}

/** Past een product (met deze slugs) bij een van de gekozen categorieën? Zonder keuze past alles. */
export function matchesCategories(slugs: ReadonlyArray<string>, selected: ReadonlyArray<string> | null | undefined): boolean {
  if (!selected || selected.length === 0) return true;
  return selected.some((sel) => (VIRTUAL_MATCH.get(sel) ?? [sel]).some((s) => slugs.includes(s)));
}

/**
 * De keuze na een klik op `slug`. Een gekozen categorie omvat zijn onderliggende: die vallen uit de keuze
 * (anders staat hetzelfde twee keer in het adres). Een tweede klik haalt de categorie weer weg.
 */
export function toggleCategory(
  selected: ReadonlyArray<string>,
  slug: string,
  tree: ReadonlyArray<CategoryNode> | null | undefined
): string[] {
  if (selected.includes(slug)) return selected.filter((s) => s !== slug);
  const drop = tree ? descendantSlugs(tree, slug) : new Set<string>();
  return [...selected.filter((s) => !drop.has(s)), slug];
}

/** Valt deze categorie al onder een andere gekozen categorie (een gekozen ouder)? Dan staat hij aan, maar niet los te zetten. */
export function isCoveredBySelection(
  slug: string,
  selected: ReadonlyArray<string>,
  tree: ReadonlyArray<CategoryNode> | null | undefined
): boolean {
  if (!tree) return false;
  return selected.some((sel) => sel !== slug && descendantSlugs(tree, sel).has(slug));
}

/** Haalt uit een keuze (bijvoorbeeld uit een handgemaakte link) wat al door een andere gekozen categorie wordt gedekt. */
export function pruneSelection(selected: ReadonlyArray<string>, tree: ReadonlyArray<CategoryNode> | null | undefined): string[] {
  return selected.filter((s) => !isCoveredBySelection(s, selected, tree));
}

/** Aantal kaarten (een per kleurvariant, zoals het raster ze toont) van een product. */
export function cardsOfProduct(p: CategoryProductLike | null | undefined): number {
  const n = Array.isArray(p?.colorOptions) ? p.colorOptions.length : 0;
  return Math.max(1, n);
}

/**
 * Aantal kaarten per categorie-slug. Een product telt voor elke slug die het draagt, dus voor een ouder is
 * het aantal gelijk aan wat het raster na een klik op die ouder toont. "Nachtmode" telt de vier takken.
 */
export function countCardsPerCategory(products: ReadonlyArray<CategoryProductLike | null | undefined>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const p of products ?? []) {
    if (!p) continue;
    const slugs = new Set(productCategorySlugs(p));
    for (const virtual of VIRTUAL_MATCH.keys()) {
      if (matchesCategories([...slugs], [virtual])) slugs.add(virtual);
    }
    const cards = cardsOfProduct(p);
    for (const s of slugs) counts.set(s, (counts.get(s) ?? 0) + cards);
  }
  return counts;
}

/**
 * Laat knopen weg waar niets in zit (aantal 0), behalve gekozen knopen en knopen met iets zichtbaars eronder:
 * een gekozen categorie die door andere keuzes op 0 komt moet zichtbaar blijven om hem weer uit te kunnen zetten.
 */
export function pruneTreeByCounts(
  nodes: ReadonlyArray<CategoryNode>,
  counts: ReadonlyMap<string, number>,
  selected: ReadonlyArray<string>
): CategoryNode[] {
  const out: CategoryNode[] = [];
  for (const n of nodes) {
    const children = pruneTreeByCounts(n.children, counts, selected);
    if ((counts.get(n.slug) ?? 0) > 0 || selected.includes(n.slug) || children.length > 0) {
      out.push({ ...n, children });
    }
  }
  return out;
}

function prettySlug(slug: string): string {
  const text = slug.replace(/-+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : slug;
}

/**
 * Leesbaar label per slug voor de chips. Veel namen komen onder meerdere takken voor ("Nachthemden" onder dames
 * en heren): alleen dan staat de naam van de bovenliggende categorie ervoor ("Dames nachtkleding › Nachthemden").
 * Een naam die maar één keer voorkomt blijft kaal. Een slug die niet in de boom staat krijgt een nette versie
 * van de slug zelf (zie categoryLabel).
 */
export function categoryLabelMap(tree: ReadonlyArray<CategoryNode> | null | undefined): Map<string, string> {
  const first = new Map<string, { label: string; parent: string }>();
  const visit = (nodes: ReadonlyArray<CategoryNode>, parent: CategoryNode | null) => {
    for (const n of nodes) {
      if (!first.has(n.slug)) first.set(n.slug, { label: n.label, parent: parent?.label ?? "" });
      visit(n.children, n);
    }
  };
  visit(tree ?? [], null);
  const perNaam = new Map<string, number>();
  for (const { label } of first.values()) perNaam.set(label, (perNaam.get(label) ?? 0) + 1);
  const labels = new Map<string, string>();
  for (const [slug, { label, parent }] of first) {
    labels.set(slug, parent && (perNaam.get(label) ?? 0) > 1 ? `${parent} › ${label}` : label);
  }
  return labels;
}

export function categoryLabel(labels: ReadonlyMap<string, string> | null | undefined, slug: string): string {
  return labels?.get(slug) ?? prettySlug(slug);
}
